import * as React from 'react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import './notes.css';
import notesService from '../../services/notesService';
import { ConfirmDialog } from '../common/ConfirmDialog';

// Types
export interface UserRef {
  id: string;
  name: string;
  role?: string;
  email?: string;
}

export interface Note {
  id: string;
  content: string;
  author: UserRef;
  createdAt: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
  deletedBy?: UserRef | null;
  editedAt?: string | null;
  editedBy?: UserRef | null;
  type?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  tags?: string[];
  isPinned?: boolean;
  isAlert?: boolean;
  alertUntil?: string | null;
  followUpRequired?: boolean;
  followUpDate?: string | null;
  followUpCompleted?: boolean;
  metadata?: Record<string, any>;
}

export interface NotesPanelProps {
  /** Entity type this notes panel is attached to */
  entityType: string;

  /** Entity ID the notes belong to */
  entityId: string;

  /** Current user information */
  currentUserId: string;
  currentUserName?: string;
  currentUserRole?: string;

  /** Current viewer permissions */
  canWrite?: boolean;         // can create own notes
  canManageAll?: boolean;     // can edit/delete any note

  /** Data hooks/services */
  fetchNotes: (opts: {
    entityType: string;
    entityId: string;
    includeDeleted: boolean
  }) => Promise<Note[]>;

  createNote: (opts: {
    entityType: string;
    entityId: string;
    content: string;
    author: UserRef;
    type?: string;
    priority?: string;
    tags?: string[];
  }) => Promise<Note>;

  updateNote: (id: string, opts: {
    content: string;
    editedBy: UserRef;
    tags?: string[];
    type?: string;
  }) => Promise<Note>;

  deleteNote: (id: string, opts: {
    deletedBy: UserRef;
    deletionReason?: string;
  }) => Promise<void>;

  restoreNote?: (id: string) => Promise<Note>;

  /** Presentation */
  title?: string; // default "Notes"
  emptyState?: React.ReactNode;
  compact?: boolean; // For condensed view in sidebars
  maxHeight?: string; // Max height before scrolling
  showTypeFilter?: boolean; // Show note type filter
  allowedTypes?: string[]; // Restrict to certain note types
  defaultType?: string; // Default type for new notes
  className?: string;

  /** Callback when unsaved note state changes (for parent warning dialogs) */
  onUnsavedChange?: (hasUnsaved: boolean) => void;

  /** Callback with the count of non-deleted notes whenever the loaded set changes */
  onCountChange?: (count: number) => void;
}

export function NotesPanel({
  entityType,
  entityId,
  currentUserId,
  currentUserName = 'Unknown User',
  currentUserRole = 'user',
  canWrite = false,
  canManageAll = false,
  fetchNotes,
  createNote,
  updateNote,
  deleteNote,
  restoreNote,
  title = 'Notes',
  emptyState,
  compact = false,
  maxHeight,
  showTypeFilter = false,
  allowedTypes,
  defaultType = 'general',
  className = '',
  onUnsavedChange,
  onCountChange,
}: NotesPanelProps) {
  const [showDeleted, setShowDeleted] = useState(false);
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isEditing, setIsEditing] = useState<{ id?: string; content: string; tags?: string[]; type?: string } | null>(null);
  // GP-651: id of the note pending delete confirmation (MUI dialog, not window.confirm)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Guard against discarding unsaved edits when switching to another note
  const [pendingEdit, setPendingEdit] = useState<{ id?: string; content: string; tags?: string[]; type?: string } | null>(null);
  const liveRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const hasLoadedOnce = useRef(false);

  // Notify parent when unsaved note state changes
  useEffect(() => {
    onUnsavedChange?.(!!isEditing && isEditing.content.trim().length > 0);
  }, [isEditing, onUnsavedChange]);

  // Notify parent of the non-deleted note count whenever the loaded set changes (e.g. badge)
  useEffect(() => {
    if (notes) onCountChange?.(notes.filter((n) => !n.deletedAt).length);
  }, [notes, onCountChange]);

  const currentUser: UserRef = {
    id: currentUserId,
    name: currentUserName,
    role: currentUserRole,
  };

  const load = useCallback(async () => {
    // Only show spinner on first load — keeps stale content visible during re-fetches
    if (!hasLoadedOnce.current) setLoading(true);
    setError(null);
    try {
      const data = await fetchNotes({
        entityType,
        entityId,
        includeDeleted: showDeleted
      });

      // Sort by pinned first, then by date
      data.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });

      setNotes(data);
      hasLoadedOnce.current = true;
    } catch (e) {
      console.error('Failed to load notes:', e);
      setError('Failed to load notes.');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [fetchNotes, entityType, entityId, showDeleted]);

  useEffect(() => {
    void load();
  }, [load]);

  // Focus the editor only when it OPENS — not on every keystroke.
  // GP-650: depending on the whole `isEditing` object re-ran this effect on each
  // keystroke (onChange creates a fresh object), and setSelectionRange snapped the
  // caret to the end of the text. Key off the edited note's identity instead, which
  // is stable while typing, so the caret stays where the user is editing.
  // `edit:<id>` for an existing note, `new` for a fresh note, null when closed —
  // a collision-free key (a real note id can never equal the literal `new`).
  const editorOpenKey = isEditing
    ? (isEditing.id !== undefined ? `edit:${isEditing.id}` : 'new')
    : null;
  useEffect(() => {
    if (!editorOpenKey || !editorRef.current) return;
    editorRef.current.focus();
    if (editorOpenKey.startsWith('edit:')) {
      // Move caret to end when opening an existing note for editing
      const len = editorRef.current.value.length;
      editorRef.current.setSelectionRange(len, len);
    }
  }, [editorOpenKey]);

  function announce(msg: string) {
    if (!liveRef.current) return;
    liveRef.current.textContent = msg;
    setTimeout(() => {
      if (liveRef.current) liveRef.current.textContent = '';
    }, 1000);
  }

  const canEdit = (n: Note) => {
    if (n.deletedAt) return false;
    return canManageAll || (canWrite && n.author.id === currentUserId);
  };

  const canDelete = (n: Note) => {
    if (n.deletedAt) return false;
    return canManageAll || (canWrite && n.author.id === currentUserId);
  };

  // True when the open editor holds changes that haven't been saved yet.
  const hasUnsavedEdits = (): boolean => {
    if (!isEditing) return false;
    if (isEditing.id !== undefined) {
      const original = notes?.find((n) => n.id === isEditing.id);
      return (original?.content ?? '') !== isEditing.content;
    }
    return isEditing.content.trim().length > 0;
  };

  // Open an editor target — but if the current editor has unsaved changes, confirm
  // first so a stray Enter / Edit click can't silently discard them.
  const requestEdit = (target: { id?: string; content: string; tags?: string[]; type?: string }) => {
    if (hasUnsavedEdits()) {
      setPendingEdit(target);
      return;
    }
    setIsEditing(target);
  };

  const fmt = (iso: string) => {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(iso));
  };

  // GP-458: hide role suffix baked into legacy author names (e.g. "Melissa Tann (pharmacist)")
  const displayAuthorName = (name?: string) =>
    (name || '').replace(/\s*\([^)]*\)\s*$/, '').trim() || name || '';

  const formatRelativeTime = (iso: string) => {
    const date = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) return fmt(iso);
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  async function handleSave() {
    if (!isEditing) return;
    const trimmed = isEditing.content.trim();
    if (!trimmed) return;

    try {
      if (isEditing.id) {
        // Update existing note
        const updated = await updateNote(isEditing.id, {
          content: trimmed,
          editedBy: currentUser,
          tags: isEditing.tags,
          type: isEditing.type,
        });
        setNotes(n => n ? n.map(x => (x.id === updated.id ? updated : x)) : n);
        announce('Note updated');
      } else {
        // Create new note (optimistic)
        const optimistic: Note = {
          id: `temp-${Date.now()}`,
          content: trimmed,
          author: currentUser,
          createdAt: new Date().toISOString(),
          type: defaultType,
          tags: isEditing.tags,
        };
        setNotes(n => (n ? [optimistic, ...n] : [optimistic]));

        const created = await createNote({
          entityType,
          entityId,
          content: trimmed,
          author: currentUser,
          type: defaultType,
          tags: isEditing.tags,
        });

        setNotes(n => n ? n.map(x => (x.id === optimistic.id ? created : x)) : n);
        announce('Note added');
      }
      setIsEditing(null);
    } catch (err) {
      console.error('Failed to save note:', err);
      setError('Failed to save note.');
      // Rollback on failure
      void load();
    }
  }

  async function handleDelete(id: string) {
    const prev = notes;
    // Optimistic removal (when not showing deleted)
    if (!showDeleted) {
      setNotes(n => n ? n.filter(x => x.id !== id) : n);
    }

    try {
      await deleteNote(id, {
        deletedBy: currentUser,
      });

      if (showDeleted) {
        // Refetch to show deleted styling
        await load();
      }
      announce('Note deleted');
    } catch (err) {
      console.error('Failed to delete note:', err);
      setNotes(prev || null);
      setError('Failed to delete note.');
    }
  }

  async function handleRestore(id: string) {
    if (!restoreNote) return;

    try {
      const restored = await restoreNote(id);
      setNotes(n => n ? n.map(x => (x.id === id ? restored : x)) : n);
      announce('Note restored');
    } catch (err) {
      console.error('Failed to restore note:', err);
      setError('Failed to restore note.');
    }
  }

  async function handleTogglePin(id: string) {
    const prev = notes;

    // Optimistic update
    setNotes(n => n ? n.map(x => (x.id === id ? { ...x, isPinned: !x.isPinned } : x)) : n);

    try {
      // Call backend API to toggle pin using notesService
      const updated = await notesService.togglePin(id);
      setNotes(n => n ? n.map(x => (x.id === id ? updated : x)) : n);
      announce(updated.isPinned ? 'Note pinned' : 'Note unpinned');
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      setNotes(prev || null);
      setError('Failed to toggle pin status.');
    }
  }

  // Filter notes by type, search query
  const filteredNotes = React.useMemo(() => {
    if (!notes) return [];

    let filtered = notes;

    // Filter by type
    if (selectedType) {
      filtered = filtered.filter(n => n.type === selectedType);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(n =>
        n.content.toLowerCase().includes(query) ||
        n.author.name.toLowerCase().includes(query) ||
        (n.tags && n.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    return filtered;
  }, [notes, selectedType, searchQuery]);

  // Get unique note types for filter
  const noteTypes = React.useMemo(() => {
    if (!notes) return [];
    const types = new Set(notes.map(n => n.type).filter(Boolean));
    return Array.from(types);
  }, [notes]);

  const getPriorityClass = (priority?: string) => {
    if (!priority) return '';
    return `priority-${priority}`;
  };

  const getTypeLabel = (type?: string) => {
    if (!type) return 'General';
    return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
  };

  return (
    <section
      aria-labelledby="notes-heading"
      className={`notes-panel ${compact ? 'notes-compact' : ''} ${className}`}
      style={{ maxHeight }}
    >
      <div className="sr-only" aria-live="polite" ref={liveRef} />

      <header className="notes-toolbar">
        {title && (
          <h2 id="notes-heading" className="notes-title">
            <span className="notes-icon">📁</span> {title}
          </h2>
        )}
        <div className="notes-actions">
          {/* Search input */}
          <input
            type="text"
            className="notes-search"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search notes"
            style={{
              padding: '0.5rem',
              border: '1px solid var(--np-border, #ccc)',
              borderRadius: '4px',
              minWidth: '200px',
              background: 'var(--np-bg, #fff)',
              color: 'var(--np-text, inherit)',
            }}
          />

          {showTypeFilter && noteTypes.length > 0 && (
            <select
              className="notes-type-filter"
              value={selectedType || ''}
              onChange={(e) => setSelectedType(e.target.value || null)}
              aria-label="Filter notes by type"
            >
              <option value="">All Types</option>
              {noteTypes.map(type => (
                <option key={type} value={type}>
                  {getTypeLabel(type)}
                </option>
              ))}
            </select>
          )}

          <label className="notes-toggle">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              aria-label="Show deleted notes"
            />
            <span>Show deleted</span>
          </label>

          {canWrite && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setIsEditing({ content: '', tags: [] })}
              aria-label="Add note"
              disabled={isEditing !== null}
            >
              + Add Note
            </button>
          )}
        </div>
      </header>

      {isEditing && (
        <div
          className="note-editor"
          role="region"
          aria-label={isEditing.id ? 'Edit note' : 'Add note'}
        >
          <label htmlFor="note-content" className="note-editor-label">
            Note Content
          </label>
          <textarea
            ref={editorRef}
            id="note-content"
            className="note-editor-textarea"
            value={isEditing.content}
            onChange={(e) => setIsEditing({ ...isEditing, content: e.target.value })}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                void handleSave();
              }
              if (e.key === 'Escape') {
                setIsEditing(null);
              }
            }}
            rows={4}
            placeholder="Enter your note here..."
            aria-describedby="note-help"
          />
          <div className="editor-buttons" style={{ marginTop: 4 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsEditing(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<SaveIcon />}
              disabled={!isEditing.content.trim()}
              onClick={() => void handleSave()}
              sx={{ fontWeight: 600 }}
            >
              {isEditing.id ? 'Save Changes' : 'Save Note'}
            </Button>
          </div>
          <div id="note-help" className="note-editor-help">
            or Ctrl+Enter to save, Esc to cancel
          </div>

          {/* Category selector - only show when editing existing notes */}
          {isEditing.id && (
            <>
              <label htmlFor="note-category" className="note-editor-label" style={{ marginTop: '1rem' }}>
                Category
              </label>
              <select
                id="note-category"
                value={isEditing.type || 'general'}
                onChange={(e) => setIsEditing({ ...isEditing, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--np-border, #ccc)',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  backgroundColor: 'var(--np-bg, white)',
                  color: 'var(--np-text, inherit)',
                }}
              >
                <option value="clinical">Clinical</option>
                <option value="billing">Billing</option>
                <option value="delivery">Delivery</option>
                <option value="general">General</option>
              </select>
            </>
          )}

          {/* Tags - only show when editing existing notes or when tags already exist */}
          {(isEditing.id || (isEditing.tags && isEditing.tags.length > 0)) && (
            <>
              <label htmlFor="note-tags" className="note-editor-label" style={{ marginTop: '0.5rem' }}>
                Tags (optional)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                {(isEditing.tags || []).map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: 'var(--np-tagchip-bg, #e0e7ff)',
                      color: 'var(--np-tagchip-fg, #3730a3)',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                    }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => {
                        const newTags = (isEditing.tags || []).filter((_, i) => i !== idx);
                        setIsEditing({ ...isEditing, tags: newTags });
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        fontSize: '1rem',
                        lineHeight: 1,
                      }}
                      aria-label={`Remove tag ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                id="note-tags"
                placeholder="Type a tag and press Enter"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                    e.preventDefault();
                    const newTag = e.currentTarget.value.trim();
                    if (newTag && !(isEditing.tags || []).includes(newTag)) {
                      setIsEditing({
                        ...isEditing,
                        tags: [...(isEditing.tags || []), newTag],
                      });
                    }
                    e.currentTarget.value = '';
                  }
                }}
              />
            </>
          )}
        </div>
      )}

      {loading && (
        <div className="notes-loading" role="status">
          <span className="spinner"></span> Loading notes...
        </div>
      )}

      {error && (
        <div className="notes-error" role="alert">
          {error}
          <button onClick={() => void load()} className="btn btn-link">
            Retry
          </button>
        </div>
      )}

      {!loading && notes && notes.length === 0 && !showDeleted && (
        <div className="notes-empty">
          {emptyState ?? 'No notes yet.'}
        </div>
      )}

      <ol className="notes-timeline" role="list">
        {(filteredNotes ?? []).map((n) => {
          const isDeleted = !!n.deletedAt;
          const isEdited = !!n.editedAt;

          return (
            <li
              key={n.id}
              className={`note-item ${isDeleted ? 'deleted' : ''} ${n.isPinned ? 'pinned' : ''}`}
            >
              <div
                className={`note-dot ${getPriorityClass(n.priority)}`}
                aria-hidden="true"
              />
              <article
                className={`note-card ${isDeleted ? 'note-deleted' : ''} ${n.isAlert ? 'note-alert' : ''}`}
                tabIndex={0}
                aria-label={`Note by ${n.author.name} on ${fmt(n.createdAt)}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canEdit(n)) {
                    requestEdit({ id: n.id, content: n.content, tags: n.tags || [], type: n.type || 'general' });
                  }
                  if ((e.key === 'Delete' || e.key === 'Backspace') && canDelete(n)) {
                    e.preventDefault(); // avoid Backspace triggering browser back-nav
                    setConfirmDeleteId(n.id);
                  }
                }}
              >
                <header className="note-header">
                  <div className="note-meta">
                    <div className="note-author-line">
                      {n.isPinned && <span className="note-pin" title="Pinned">📌</span>}
                      {n.isAlert && <span className="note-alert-icon" title="Alert">⚠️</span>}
                      {n.followUpRequired && !n.followUpCompleted && (
                        <span className="note-followup" title="Follow-up required">🔔</span>
                      )}
                      <span className="note-author">{displayAuthorName(n.author.name)}</span>
                      {/* GP-458: role intentionally hidden — show author name only */}
                      {n.type && n.type !== 'general' && (
                        <span className={`note-type note-type-${n.type}`}>
                          {getTypeLabel(n.type)}
                        </span>
                      )}
                    </div>
                    <div className="note-date">
                      <span title={fmt(n.createdAt)}>
                        {formatRelativeTime(n.createdAt)}
                      </span>
                      {isEdited && (
                        <span
                          className="note-edited"
                          title={
                            n.editedBy
                              ? `Edited ${fmt(n.editedAt!)} by ${displayAuthorName(n.editedBy.name)}`
                              : `Edited ${fmt(n.editedAt!)}`
                          }
                        >
                          {' '}
                          {n.editedBy
                            ? `(edited by ${displayAuthorName(n.editedBy.name)})`
                            : '(edited)'}
                        </span>
                      )}
                      {isDeleted && (
                        <span className="note-deleted-flag">
                          {' '}(Deleted {fmt(n.deletedAt!)})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="note-actions">
                    {!isDeleted && canEdit(n) && (
                      <>
                        <button
                          className="icon-btn"
                          aria-label={n.isPinned ? "Unpin note" : "Pin note"}
                          title={n.isPinned ? "Unpin note" : "Pin note to top"}
                          onClick={() => void handleTogglePin(n.id)}
                        >
                          {n.isPinned ? '📌' : '📍'}
                        </button>
                        <button
                          className="icon-btn"
                          aria-label="Edit note"
                          title="Edit note"
                          onClick={() => requestEdit({ id: n.id, content: n.content, tags: n.tags || [], type: n.type || 'general' })}
                        >
                          ✏️
                        </button>
                        <button
                          className="icon-btn"
                          aria-label="Delete note"
                          title="Delete note"
                          onClick={() => setConfirmDeleteId(n.id)}
                        >
                          🗑️
                        </button>
                      </>
                    )}
                    {isDeleted && restoreNote && canManageAll && (
                      <button
                        className="icon-btn"
                        aria-label="Restore note"
                        title="Restore note"
                        onClick={() => void handleRestore(n.id)}
                      >
                        ↩️
                      </button>
                    )}
                  </div>
                </header>
                <div className="note-content">
                  {n.content.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < n.content.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
                {n.tags && n.tags.length > 0 && (
                  <div className="note-tags">
                    {n.tags.map(tag => (
                      <span key={tag} className="note-tag">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                {n.followUpDate && !n.followUpCompleted && (
                  <div className="note-followup-date">
                    Follow-up by: {fmt(n.followUpDate)}
                  </div>
                )}
              </article>
            </li>
          );
        })}
      </ol>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete note"
        message="Are you sure you want to delete this note?"
        confirmLabel="Delete"
        destructive
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => {
          const id = confirmDeleteId;
          setConfirmDeleteId(null);
          if (id) void handleDelete(id);
        }}
      />

      <ConfirmDialog
        open={pendingEdit !== null}
        title="Discard unsaved changes?"
        message="You have unsaved changes to the current note. Switch anyway and discard them?"
        confirmLabel="Discard & switch"
        destructive
        onClose={() => setPendingEdit(null)}
        onConfirm={() => {
          setIsEditing(pendingEdit);
          setPendingEdit(null);
        }}
      />
    </section>
  );
}